import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

interface CopyableIdProps {
  id: string;
  label?: string;
  className?: string;
  showLabel?: boolean;
  codeClassName?: string;
  labelClassName?: string;
}

/**
 * A component that displays an ID with a copy-to-clipboard button.
 * Shows a checkmark briefly after successful copy.
 */
export const CopyableId: React.FC<CopyableIdProps> = ({
  id,
  label = 'ID',
  className = '',
  showLabel = true,
  codeClassName,
  labelClassName,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className={className}>
      {showLabel && (
        <label
          className={`text-sm font-medium text-muted-foreground ${labelClassName || ''}`}
        >
          {label}
        </label>
      )}
      <div className="flex items-center gap-2 mt-1">
        <code
          className={`${codeClassName || 'text-sm'} font-mono bg-muted px-2 py-1 rounded`}
        >
          {id}
        </code>
        <button
          onClick={handleCopy}
          className="p-1 hover:bg-muted rounded transition-colors"
          title={copied ? 'Copied!' : 'Copy to clipboard'}
        >
          {copied ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          )}
        </button>
      </div>
    </div>
  );
};
